import { useEffect, useState } from 'react';

import { useRutilusMutation } from '../../../api/graphql/useRutilusMutation';
import { useAnalytics } from '../../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../../helpers/analytics/trackEvent';
import { reportMykissClientError } from '../../../helpers/errorHandling/client';
import { useUserInfo } from '../../../helpers/hooks/useUserInfo';
import { FollowRecordsTypeEnum } from '../../../interfaces/graphql';
import { followRecordsMutation } from '../../../mutations/rutilus/FollowRecords';
import { unfollowRecordsMutation } from '../../../mutations/rutilus/UnfollowRecords';
import { AppDownloadModal } from '../../AppDownloadModal/AppDownloadModal';

import { Button, IButtonProps } from './Button';

interface IProps {
  type: FollowRecordsTypeEnum;
  id: string;
  source: string;
  isFollowed?: boolean;
  buttonProps?: IButtonProps;
  customAppDownloadHeader?: string;
}

export const GQLFollowButton = ({
  isFollowed,
  id,
  type,
  source,
  buttonProps = {},
  customAppDownloadHeader,
}: IProps) => {
  const { isLoggedIn } = useUserInfo();
  const [isLoading, setIsLoading] = useState(false);
  const [_isFollowed, setIsFollowed] = useState(isFollowed);
  const { currentPage } = useAnalytics();

  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [toggleFollow] = useRutilusMutation(
    _isFollowed ? unfollowRecordsMutation : followRecordsMutation,
  );

  useEffect(() => {
    setIsFollowed(isFollowed);
  }, [isFollowed]);

  const onClick = (): void => {
    if (isLoggedIn) {
      setIsLoading(true);
      toggleFollow({ variables: { input: { ids: [id], type } } })
        .then(() => {
          setIsFollowed(!_isFollowed);
          setIsLoading(false);
          if (type === FollowRecordsTypeEnum.FISHING_WATER && !_isFollowed) {
            void trackEvent({
              name: 'fishing_water_followed',
              properties: { source: 'water_details' },
            });
          }
          if (type === FollowRecordsTypeEnum.PAGE) {
            void trackEvent({
              name: _isFollowed ? 'page_unfollowed' : 'page_followed',
              // @ts-ignore
              properties: {},
            });
          }
          if (type === FollowRecordsTypeEnum.SPECIES && !_isFollowed) {
            void trackEvent({
              name: 'species_followed',
              properties: { source: 'species_details', id },
            });
          }
          if (type === FollowRecordsTypeEnum.USER && !_isFollowed) {
            void trackEvent({
              name: 'angler_followed',
              properties: {
                source: 'angler_page',
              },
            });
          }
        })
        .catch(error => {
          reportMykissClientError(error);
          setIsLoading(false);
        });
    } else {
      void trackEvent({
        name: 'mykiss_cta_button_clicked',
        properties: {
          source,
          source_id: id,
          type: 'downloadModal',
          page: currentPage,
        },
      });
      setIsAppDownloadModalOpen(true);
    }
  };

  return (
    <>
      <Button
        variant={_isFollowed ? 'buttonSecondary' : 'buttonPrimary'}
        onClick={onClick}
        disabled={isLoading}
        size="sm"
        data-testid="GQLFollowButton"
        {...buttonProps}
      >
        {_isFollowed ? 'Following' : 'Follow'}
      </Button>
      <AppDownloadModal
        appDownloadSource={source}
        appDownloadSourceId={id}
        modalHeader={customAppDownloadHeader ?? 'Sign up to follow'}
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
      />
    </>
  );
};
